import { useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrAnlageDetail } from './typen';
import { GEWERK, herkunftMarke, z } from './gemeinsam';
import { SchemaBild } from './SchemaBild';
import { DatenpunkteTabelle } from './AggregatDetail';
import { Funktionsbeschreibung } from './Funktionsbeschreibung';
import { Einstellwerte } from './Einstellwerte';
import { PdfKnopf } from './PdfKnopf';
import { Befunde } from './Befunde';
import {
  ContentSection, EmptyState, ErrorState, LoadingState, PropertyGrid, StatusBadge,
} from '../../muster';

const KATEGORIE_TEXT: Record<string, string> = {
  eingabe_ausgabe: 'Eingabe/Ausgabe', verarbeitung: 'Verarbeitung',
  bedienen_beobachten: 'Bedienen/Beobachten', management: 'Management',
};
const KATEGORIE_REIHENFOLGE = [
  'eingabe_ausgabe', 'verarbeitung', 'bedienen_beobachten', 'management',
];

/**
 * Eine GA-Anlage im Detail — Baum-Reiter, Auswahl „Anlage". Kopf, Schema,
 * Aggregate (aufklappbar mit ihren Datenpunkten), Funktionsliste,
 * Funktionsbeschreibung.
 */
export function AnlageDetail({
  api, projektId, anlage, onNeuLaden,
}: {
  api: ApiClient;
  projektId: string;
  anlage: MsrAnlageDetail | null;
  onNeuLaden: () => void;
}) {
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [erzeugtLaeuft, setErzeugtLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [schemaArt, setSchemaArt] = useState<'msr' | 'funktion'>('msr');

  if (!anlage) return <LoadingState text="Anlage wird abgerufen …" zeilen={6} spalten={3} />;

  const toggle = (id: string) => setOffen((o) => {
    const neu = new Set(o);
    if (neu.has(id)) neu.delete(id); else neu.add(id);
    return neu;
  });

  const nachKategorie = KATEGORIE_REIHENFOLGE.map((k) => ({
    kategorie: k,
    funktionen: anlage.funktionen.filter((f) => f.kategorie === k),
  })).filter((g) => g.funktionen.length > 0);

  const beschreibungErzeugen = async (force: boolean) => {
    setErzeugtLaeuft(true);
    setFehler(null);
    try {
      await api.msrBeschreibungErzeugen(projektId, anlage.id, force);
      onNeuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setErzeugtLaeuft(false);
    }
  };

  return (
    <>
      <ContentSection
        titel={anlage.kennung}
        beschreibung={anlage.bezeichnung ?? undefined}
        meta={<StatusBadge zustand="neutral" text={GEWERK[anlage.gewerk]} />}
        aktionen={(
          <PdfKnopf api={api} projektId={projektId} art="schema" ziel="anlage"
            zielId={anlage.id} label="Schema" />
        )}
      >
        <PropertyGrid
          spalten={3}
          eintraege={[
            { beschriftung: 'Anlagenart', wert: anlage.anlagenart, herkunft: 'modell' },
            { beschriftung: 'Geschoss', wert: anlage.geschoss ?? null },
            { beschriftung: 'Planungsbereich', wert: anlage.planungsbereich ?? null },
            { beschriftung: 'ASP', wert: anlage.aspKennung ?? null },
            { beschriftung: 'Feldgeräte', wert: anlage.feldgeraete },
            { beschriftung: 'Funktionen', wert: anlage.funktionen.length },
            ...Object.entries(anlage.merkmale ?? {}).map(([k, v]) => ({
              beschriftung: k,
              wert: typeof v === 'boolean' ? (v ? 'ja' : 'nein') : String(v),
              herkunft: 'modell' as const,
            })),
          ]}
        />
      </ContentSection>

      <ContentSection titel="Schema">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
          <span className="schwach" style={{ fontSize: 12 }}>Ansicht:</span>
          <button type="button" className={`reiter-knopf${schemaArt === 'msr' ? ' aktiv' : ''}`}
            style={{ fontSize: 12 }} onClick={() => setSchemaArt('msr')}>
            MSR-Schema (Vorlage)
          </button>
          <button type="button" className={`reiter-knopf${schemaArt === 'funktion' ? ' aktiv' : ''}`}
            style={{ fontSize: 12 }} onClick={() => setSchemaArt('funktion')}>
            Funktionsschema
          </button>
        </div>

        {schemaArt === 'msr'
          ? (
            <SchemaBild
              titel="MSR-Schema"
              schluessel={`msr-${anlage.id}`}
              laden={() => api.msrSchemaAnlageSvg(projektId, anlage.id)}
            />
          )
          : (
            <SchemaBild
              titel="Funktionsschema"
              schluessel={`funktion-${anlage.id}`}
              laden={() => api.msrSchemaAnlageFunktionsschemaSvg(projektId, anlage.id)}
            />
          )}
      </ContentSection>

      <ContentSection titel="Aggregate" meta={`${z(anlage.aggregate.length)} Aggregate`}>

        {anlage.aggregate.map((ag) => (
          <div key={ag.id} style={{ marginBottom: 6 }}>
            <button type="button" className="baum-ebene" style={{ width: '100%' }}
              onClick={() => toggle(ag.id)}>
              <span>{offen.has(ag.id) ? '▾' : '▸'}</span>
              <span>{ag.aggregat}</span>
              {ag.bezeichnung && <span className="schwach">{ag.bezeichnung}</span>}
              <span className="schwach" style={{ marginLeft: 'auto' }}>
                {z(ag.datenpunkte.length)} Datenpunkte
                {ag.geschaetzt > 0 && ` · ${z(ag.geschaetzt)} geschätzt`}
              </span>
            </button>

            {offen.has(ag.id) && (
              <div style={{ padding: '6px 0 6px 20px' }}>
                <DatenpunkteTabelle zeilen={ag.datenpunkte} />
              </div>
            )}
          </div>
        ))}

        {anlage.aggregate.length === 0 && (
          <EmptyState
            titel="Für diese Anlage ist kein Aggregat abgeleitet."
            erklaerung={'Aggregate entstehen aus den Bauteilen des Gewerkemodells — ohne '
              + 'Ableitungslauf gibt es keine.'}
          />
        )}
      </ContentSection>

      <ContentSection titel="Funktionsliste">

        {nachKategorie.map((g) => (
          <div key={g.kategorie} style={{ marginBottom: 10 }}>
            <div className="feldname">{KATEGORIE_TEXT[g.kategorie] ?? g.kategorie}</div>
            <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
              {g.funktionen.map((f) => (
                <li key={f.id} style={{ fontSize: 13 }}>
                  {f.funktion}{' '}
                  <StatusBadge {...herkunftMarke(f.herkunft)} klein />
                </li>
              ))}
            </ul>
          </div>
        ))}

        {nachKategorie.length === 0 && (
          <EmptyState
            titel="Für diese Anlage ist keine GA-Funktion abgeleitet."
            erklaerung="Ohne Aggregat greift keine Funktionsvorlage."
          />
        )}
      </ContentSection>

      {fehler && (
        <ErrorState
          meldung="Die Funktionsbeschreibung konnte nicht erzeugt werden."
          technisch={fehler}
          beiErneutVersuchen={() => void beschreibungErzeugen(true)}
        />
      )}

      <Funktionsbeschreibung
        beschreibung={anlage.beschreibung}
        onErzeugen={beschreibungErzeugen}
        erzeugtLaeuft={erzeugtLaeuft}
      />

      <Einstellwerte api={api} projektId={projektId} anlageId={anlage.id} />

      <Befunde api={api} projektId={projektId} ziel={anlage.id}
        titel="Befunde dieser Anlage"
        leerText="Keine Befunde an dieser Anlage." />
    </>
  );
}
